import type { ActiveSession } from "../domain/types";
import { isAndroid } from "./device";

/**
 * The one-way channel to the Kaya Steps app: an Android `intent:` URL.
 *
 * Why it exists: background sampling runs every 15 minutes, which is WorkManager's floor, and
 * measuring a window needs a reading on each side of it. A 12-minute walk can therefore fall
 * between two samples and never be measurable. A ping at each boundary gives the window the exact
 * readings it needs, so a short walk is as precise as a long one.
 *
 * Sent as an `intent:` URL rather than a bare custom scheme: the intent syntax carries a fallback,
 * so a phone without the app installed stays where it is instead of showing an error. The app's
 * handler has no UI — it does its work and finishes straight back to the browser.
 *
 * Chrome only follows one of these from a user gesture, so every caller sits inside a click or
 * within the few seconds of activation that follow one. A ping fired from a bare effect on a
 * relaunch is dropped silently — which is why a resumed session is not re-announced until the
 * user touches something.
 */
/** The intent URL for one ping. Pure, so the fallback rule below has a test. */
export function intentUrl(query: string, fallbackHref: string | null): string {
  // The fallback is the page itself, which the browser *reloads* when no app answers. A running
  // session survives that (its record is read back on mount); a `stop` must not carry one — see
  // `pingSession` — and a Refresh tap should not cost a session either, so `sample` sends none.
  const fallback = fallbackHref ? `S.browser_fallback_url=${encodeURIComponent(fallbackHref)};` : "";
  return `intent://session?${query}#Intent;scheme=kayasteps;package=com.kaya.steps;${fallback}end`;
}

function send(query: string, withFallback = true): void {
  if (!isAndroid()) return;
  try {
    window.location.href = intentUrl(query, withFallback ? window.location.href : null);
  } catch {
    /* no app, or a browser that refuses the scheme — the 15-minute sampling still applies */
  }
}

export type SessionPing = "start" | "stop" | "resume" | "pause";

/**
 * What a live session is and how long a step is, for the counter app's notification: it shows
 * the elapsed time, the steps and "~1.2 km", and its tap opens the session's own screen. The
 * stride is the web app's (`energy.ts::strideMeters`, from height or a declared walk) — the app
 * has no profile of its own, and the API is not asked on every tick. (ADR-062)
 */
export interface SessionExtra {
  kind?: "walk" | "treadmill" | "intervals";
  /** metres a step */
  stride?: number;
  /** a free walk's planned minutes, for the wrapper's "Half-way" cue; omitted when open (ADR-063) */
  plan?: number | null;
  /** the wrapper speaks the cues: the player's voice switch is on */
  cues?: boolean;
  lang?: "en" | "hi";
}

/** The query one ping sends; `sessionIntent` is what reaches the browser. */
export function sessionQuery(action: SessionPing, at: number, sid?: string, extra?: SessionExtra): string {
  const kind = extra?.kind ? `&kind=${extra.kind}` : "";
  const stride =
    typeof extra?.stride === "number" && Number.isFinite(extra.stride) && extra.stride > 0
      ? `&stride=${extra.stride.toFixed(3)}`
      : "";
  const plan = typeof extra?.plan === "number" && extra.plan > 0 ? `&plan=${Math.round(extra.plan)}` : "";
  const cues = extra?.cues ? "&cues=1" : "";
  const lang = extra?.lang ? `&lang=${extra.lang}` : "";
  return `action=${action}&t=${Math.round(at)}${sid ? `&sid=${encodeURIComponent(sid)}` : ""}${kind}${stride}${plan}${cues}${lang}`;
}

/**
 * A `stop` carries no fallback. It is the one ping sent when the session record is gone (a
 * finish clears it a moment later, and it is often sent from a timer with no gesture behind it),
 * and a fallback reload lands the player back on its own route with nothing to resume — so it
 * begins a fresh session from phase one, and a finished treadmill run "restarted" instead of
 * showing its done screen. Without one, an unanswered intent is simply dropped. (ADR-035)
 */
export function sessionIntent(
  action: SessionPing,
  at: number,
  sid: string | undefined,
  href: string,
  extra?: SessionExtra
): string {
  return intentUrl(sessionQuery(action, at, sid, extra), action === "stop" ? null : href);
}

/**
 * Tells the counter app that a session started, paused, resumed or ended. A pause stops the
 * notification's clock and the wrapper's cues; the resume after it moves the start on by the
 * paused time (ADR-064).
 *
 * `at` is the moment the *web app* opened the window. The reading itself is stamped when the
 * counter app gets round to taking it, a second or two later, and a boundary reading stamped after
 * the boundary is not a boundary reading — it left the walk screen unable to find a baseline at
 * all. Passing the real edge lets the app stamp it correctly.
 *
 * `sid` names the session. The app treats a `start` it has already seen as a resume, so a screen
 * that mounts twice cannot zero the count, and a `stop` ends whatever is live regardless of which
 * session it names — an orphaned counter is worse than an over-eager stop. (ADR-027)
 */
export function pingSession(action: SessionPing, at: number = Date.now(), sid?: string, extra?: SessionExtra): void {
  send(sessionQuery(action, at, sid, extra), action !== "stop");
}

/**
 * Asks the counter app for a fresh reading right now, uploaded straight away.
 *
 * The scheduled sample is a quarter of an hour apart at best and dies with the app on some ROMs;
 * this is the button that does not depend on either. Nothing is started and nothing is left
 * behind: the app reads, posts, re-arms its schedule and finishes.
 */
export function pingSample(): void {
  send(`action=sample&t=${Date.now()}`, false);
}

/**
 * The query that arms or clears the counter app's water alarms: today's reminder times, or none.
 * `limit`: the target is a doctor's ceiling, so an alarm that cannot reach the server must not ask
 * for more. Pure, so the shape the app parses has a test.
 */
export function remindQuery(slots: string[] | null, at: number, limit = false): string {
  const list = (slots || []).filter((s) => /^\d{2}:\d{2}$/.test(s));
  return `action=remind&t=${Math.round(at)}&slots=${encodeURIComponent(list.join(","))}${limit ? "&limit=1" : ""}`;
}

/**
 * Hands the counter app the reminder times, so its own inexact alarms can stand in when a push
 * does not arrive — the vivo is known to drop them (ADR-045). An empty list clears them.
 *
 * The app does not simply post at these times: each alarm fires a few minutes after its slot,
 * stays quiet if a push reached the phone meanwhile, and otherwise asks the server whether the
 * reminder is still due. So arming them beside a working push costs nothing.
 *
 * No fallback URL, whatever the gesture: on a phone without the counter app a fallback would
 * reload the settings page in the middle of turning reminders on, for a ping whose absence loses
 * nothing (ADR-035). Every caller sits in a tap; one sent after the tap's activation has lapsed
 * is dropped, and the next settings change sends it again.
 */
export function pingRemind(slots: string[] | null, limit = false): void {
  send(remindQuery(slots, Date.now(), limit), false);
}

/**
 * What every `start` and `resume` for a session tells the counter app (C5, ADR-063): a missing
 * `plan`, `cues` or `lang` reads there as open, off and English, so a resume that left them out
 * would silence the wrapper mid-walk. The treadmill sends its kind and stride only, as it always has.
 */
export function sessionExtra(
  s: Pick<ActiveSession, "kind" | "mode" | "plannedMin">,
  stride: number,
  player: { voice: boolean; lang: "en" | "hi" }
): SessionExtra {
  if (s.kind === "treadmill") return { kind: "treadmill", stride };
  const intervals = s.mode === "intervals";
  return {
    kind: intervals ? "intervals" : "walk",
    stride,
    plan: intervals ? null : (s.plannedMin ?? null),
    cues: player.voice,
    lang: player.lang,
  };
}

/**
 * Re-announces an open walk or run from a tap elsewhere (Today's hero, the LiveBar). Never while
 * it is paused: a `resume` moves the wrapper's clock on, and a paused walk resumed only on the
 * counter app would count and speak through the pause. (The treadmill sends no pause, so its
 * resume stays as it was.) A workout has no counter session.
 */
export function resumeSessionPing(s: ActiveSession, stride: number, player: { voice: boolean; lang: "en" | "hi" }): void {
  if (s.kind === "workout" || (s.kind === "walk" && s.since === null)) return;
  pingSession("resume", Date.now(), s.sid, sessionExtra(s, stride, player));
}
